import { type FunctionComponent, useCallback } from 'react'
import { useIntl } from 'react-intl'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { useHotkeys, useOs } from '@mantine/hooks'
import { NavigationBar } from '@retailer-platform/shared-components/src/tds'
import { ICPlatformVersion } from '../../../utils/ic-platform/versioning/versions.constants'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { useTopBarMenu } from './TopBarMenuContext'
import { TdsNavSearchMenu } from './TdsNavSearchMenu'
import { type SpotlightActionV3 } from './searchV3.types'

const SearchAccessControlConfig: AccessControlConfig = {
  featureToggles: ['rt_ipp_nav_search' as FeatureToggle],
  minICPlatformVersion: ICPlatformVersion.r2025_03_0,
}

type Props = {
  // Pre-collected nav actions for the dropdown. Caller picks the source
  // (retailer vs admin) so the same button works in both shells.
  actions: SpotlightActionV3[]
}

export const TdsSearchButton: FunctionComponent<Props> = ({ actions }) => {
  const intl = useIntl()
  const { isOpen, setIsOpen } = useTopBarMenu('search')
  const hasAccess = useAccessControl()
  const trackEvent = useTrackEvent()
  const os = useOs()

  const openSearch = useCallback(() => setIsOpen(true), [setIsOpen])

  useHotkeys([
    ['mod+k', openSearch],
    ['ctrl+k', openSearch],
  ])

  const onMenuStateChange = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      if (next) {
        trackEvent({
          id: 'navV3.searchSpotlight.open',
          description: 'User is opening the search bar',
        })
      } else {
        trackEvent({
          id: 'navV3.searchSpotlight.close',
          description: 'User is closing the search bar',
        })
      }
    },
    [setIsOpen, trackEvent]
  )

  if (!hasAccess(SearchAccessControlConfig)) {
    return null
  }

  const isMac = os === 'macos' || os === 'ios'
  const tooltipHotkey = isMac ? '⌘K' : 'Ctrl+K'

  const trigger = (
    <NavigationBar.Button
      aria-label={`${intl.formatMessage({
        id: isMac ? 'navV3.search.button.tooltipMac' : 'navV3.search.button.tooltipWindows',
      })} (${tooltipHotkey})`}
      icon="search"
      onPress={() => onMenuStateChange(!isOpen)}
    />
  )

  return (
    <TdsNavSearchMenu
      isOpen={isOpen}
      onChangeOpen={onMenuStateChange}
      trigger={trigger}
      actions={actions}
    />
  )
}
